import { SyncOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { leaveRequestApi } from 'Api/leaveRequest';
import { PermissionContext } from 'components/Global/AuthLayout';
import Buttons from 'components/Global/Buttons';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import TableActionHeader from 'components/Global/TableActionHeader';
import dayjs from 'dayjs';
import { TablePagination, TableSorter } from 'interfaces/global.interface';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toastText } from 'utils/utils';
import AddLeaveRequestModal from './AddLeaveRequestModal';
import LeaveRequestHeader from './Header';
import styles from './index.module.scss';
import LeaveRequestTable from './LeaveRequestTable';

const LeaveRequestComponent = (props: any) => {
	const [LeaveRequestData, setLeaveRequestData] = useState<any[]>([]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState('');
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deleteLeaveRequestId, setDeleteLeaveRequestId] = useState<
		string | null
	>(null);

	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
	const [isAddLeaveRequestModalOpen, setIsAddLeaveRequestModalOpen] =
		useState(false);
	const [edit, setEdit] = useState<any>(null);
	const [searchText, setSearchText] = useState('');
	const [statusFilterValue, setStatusFilterValue] = useState('');
	const [date, setDate] = useState([dayjs().startOf('year'), dayjs()]);
	const [employeeId, setEmployeeId] = useState<string | null>(null);
	const [searchParams] = useSearchParams();
	const permissionContext = useContext(PermissionContext);
	const isAdd =
		permissionContext.allowedPermissions.includes('Add_Leave_Request');

	const debouncedSearchValue = useDebounce(searchText, 500);

	const buttons = [
		{
			text: 'Apply for leave',
			isLoading: false,
			className: 'btn-blue',
			// icon: <AddSvg />,
			isSubmit: true,
			onclick: () => {
				setIsAddLeaveRequestModalOpen(true);
			},
			disabled: isLoading,
		},
	];
	const showDeleteModal = () => {
		setIsDeleteModalOpen(true);
	};

	const deleteHandler = async () => {
		if (deleteLeaveRequestId) {
			try {
				setIsDeleteLoading(true);
				const response = await leaveRequestApi.deleteLeaveRequest(
					deleteLeaveRequestId
				);
				toastText(response.data.message, 'success');
				cancelDeleteModal();
				fetchLeaveRequest();
			} catch (error: any) {
				let message =
					error?.response?.data?.message ||
					'Something went wrong in delete Leave Request';
				toastText(message, 'error');
			} finally {
				setIsDeleteLoading(false);
			}
		}
	};
	const handleEdit = (leaveObj: any) => {
		setIsAddLeaveRequestModalOpen(true);
		setEdit(leaveObj);
	};
	const tableChangeHandler = (
		pagination: TablePagination,
		filters: any,
		sorter: TableSorter
	) => {
		setTotalRecords(pagination.total);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
		let order = '';
		if (sorter?.order === 'ascend') {
			order = 'asc';
		} else if (sorter?.order) {
			order = 'desc';
		}
		setSortOrder(order);
		setSortField(sorter.field);
	};
	const fetchLeaveRequest = async () => {
		try {
			const query: any = {
				page: currentPage,
				searchText: debouncedSearchValue.trim(),
				sortBy: sortField,
				sortOrder: sortOrder,
				pageSize: pageSize,
				status: statusFilterValue,
				employeeId: employeeId,
			};

			if (date && date.length > 0) {
				query.startDate = date[0].format('YYYY-MM-DD');
				query.endDate = date[1].format('YYYY-MM-DD');
			}
			setIsLoading(true);
			const leaveRequest = await leaveRequestApi.getLeaveRequests(query);
			setLeaveRequestData(leaveRequest.data.data);
			setTotalRecords(leaveRequest.data.total);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching leave request';

			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const handleSearch = (value: string) => {
		setSearchText(value);
		setCurrentPage(1);
	};

	const handleSearchApi = async (value: string) => {
		//search api
	};

	const handleCancel = () => {
		setIsAddLeaveRequestModalOpen(false);
	};

	const cancelDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setDeleteLeaveRequestId(null);
	};

	const handleOk = () => {
		setIsDeleteModalOpen(false);
		setDeleteLeaveRequestId(null);
	};

	const handleStatusFilter = (value: string) => {
		setStatusFilterValue(value);
	};

	const handleDatePicker = (value: any) => {
		setDate(value);
	};

	const refreshLeaveRequest = () => {
			setCurrentPage(1)
			fetchLeaveRequest()
	}

	useEffect(() => {
		fetchLeaveRequest();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		currentPage,
		sortField,
		sortOrder,
		pageSize,
		debouncedSearchValue,
		statusFilterValue,
		date,
		employeeId,
	]);

	useEffect(() => {
		const employeeId = searchParams.get('employeeId');
		const startDate = searchParams.get('startDate');
		const endDate = searchParams.get('endDate');

		if (startDate && endDate && employeeId) {
			setEmployeeId(employeeId);
			setDate([dayjs(startDate), dayjs(endDate)]);
		}
	}, [searchParams]);

	return (
		<>
			<div
				style={{
					maxHeight: 'calc(100vh - 100px)',
				}}
			>
				<TableActionHeader title={'Employees'}>
					<div>
						<div
							className={styles['leave-request-header__buttons']}
						>
							<Tooltip title="Refresh all the Leave Request">
								<SyncOutlined
									style={{
										fontSize: '20px',
										cursor: isLoading
											? 'not-allowed'
											: 'pointer',
										color: '#584495',
										transition: 'transform 0.3s',
										marginRight: '10px',
									}}
									onClick={
										!isLoading
											? refreshLeaveRequest
											: undefined
									}
								/>
							</Tooltip>
							{isAdd && <Buttons buttons={buttons} />}
						</div>
					</div>
				</TableActionHeader>
			</div>
			<div className={styles['leave-request-container']}>
				<LeaveRequestHeader
					searchText={searchText}
					handleSearch={handleSearch}
					handleSearchApi={handleSearchApi}
					StatusFilter={statusFilterValue}
					handleStatusFilter={handleStatusFilter}
					handleDatePicker={handleDatePicker}
					dateValue={date}
				/>
				<LeaveRequestTable
					tableData={LeaveRequestData}
					totalRecords={totalRecords}
					currentPage={currentPage}
					pageSize={pageSize}
					isLoading={isLoading}
					tableChangeHandler={tableChangeHandler}
					showDeleteModal={showDeleteModal}
					setDeleteLeaveRequestId={setDeleteLeaveRequestId}
					handleEdit={handleEdit}
				/>
			</div>

			{isAddLeaveRequestModalOpen &&
<AddLeaveRequestModal
				isAddLeaveRequestModalOpen={isAddLeaveRequestModalOpen}
				handleCancel={handleCancel}
				fetchLeaveRequest={fetchLeaveRequest}
				edit={edit}
				setEdit={setEdit}
			/>
			}
			<ConfirmDelete
				isModalOpen={isDeleteModalOpen}
				handleCancel={cancelDeleteModal}
				deleteHandler={deleteHandler}
				isLoading={isDeleteLoading}
				handleOk={handleOk}
			/>
		</>
	);
};

export default LeaveRequestComponent;
